import { useRef, useEffect, useState } from "react";
import Typography, { ITypo, ITypoColor } from "components/design_system/Typography";
import Button from "components/design_system/Button";
import classes from "./classes.module.scss";
import { useTranslate } from "hooks/useTranslate";

export default function ScanData() {
	const { translate } = useTranslate();
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [downloadCompleted, setDownloadCompleted] = useState(false);
	const downloadInitiatedRef = useRef(false);

	// Function to handle CSV download
	const handleDownloadCSV = async () => {
		try {
			setIsLoading(true);
			setError(null);

			const response = await fetch(`${process.env.REACT_APP_API_URL}/api/scan-datas/export-csv`, {
				method: "GET",
				headers: {
					Authorization: `Bearer ${process.env.REACT_APP_API_KEY}`,
				},
			});

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`);
			}

			// Get the blob from the response
			const blob = await response.blob();

			// Create a URL for the blob
			const url = window.URL.createObjectURL(blob);

			// Create a temporary link element
			const a = document.createElement("a");
			a.href = url;

			// Set a filename
			const contentDisposition = response.headers.get("Content-Disposition");
			let filename = "scan-data.csv";

			if (contentDisposition) {
				const filenameMatch = contentDisposition.match(/filename="(.+)"/);
				if (filenameMatch && filenameMatch.length > 1) {
					filename = filenameMatch[1];
				}
			}

			a.download = filename;

			// Append to the document, click, and clean up
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);

			// Release the object URL
			window.URL.revokeObjectURL(url);
			setIsLoading(false);
			setDownloadCompleted(true);
		} catch (error) {
			console.error("Error downloading CSV:", error);
			setError("Error downloading CSV. Please try again.");
			setIsLoading(false);
		}
	};

	// Download CSV automatically when component mounts - with ref to prevent multiple downloads
	useEffect(() => {
		// Only download if it hasn't been initiated yet
		if (!downloadInitiatedRef.current) {
			downloadInitiatedRef.current = true;
			handleDownloadCSV();
		}
	}, []);

	// Retry download if there was an error - this is triggered by user action
	const handleRetry = () => {
		handleDownloadCSV();
	};

	// Manual download triggered by user action
	const handleManualDownload = () => {
		handleDownloadCSV();
	};

	return (
		<div className={classes.root}>
			<div className={classes.container}>
				<Typography typo={ITypo.DISPLAY_XSMALL} className={classes.title}>
					{translate("pages.scan_data.title") || "Scan Data Export"}
				</Typography>

				<div className={classes.statusSection}>
					{isLoading ? (
						<Typography typo={ITypo.TEXT_LG_MEDIUM} className={classes.statusText}>
							{translate("pages.scan_data.downloading") || "Downloading CSV data..."}
						</Typography>
					) : downloadCompleted && !error ? (
						<>
							<Typography typo={ITypo.TEXT_LG_MEDIUM} className={classes.statusText}>
								{translate("pages.scan_data.download_completed") || "Download completed!"}
							</Typography>
							<Typography typo={ITypo.TEXT_MD_MEDIUM} className={classes.infoText}>
								{translate("pages.scan_data.download_again") || "If the download didn't start automatically, click the button below."}
							</Typography>
							<Button onClick={handleManualDownload}>{translate("pages.scan_data.download_again_button") || "Download Again"}</Button>
						</>
					) : error ? (
						<>
							<div className={classes.error}>
								<Typography typo={ITypo.TEXT_MD_MEDIUM} color={ITypoColor.RED}>
									{error}
								</Typography>
							</div>
							<Button onClick={handleRetry}>{translate("pages.scan_data.retry") || "Retry Download"}</Button>
						</>
					) : null}
				</div>
			</div>
		</div>
	);
}
